import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { markPosted } from "@/lib/pipelineApi";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export const canMarkPosted = (c) => c.kind !== "lead" && !!c.ids?.horse_id && ["contracts_ads", "ready"].includes(c.stage)
  && (!c.ids?.application_id || ["approved", "scheduled", "unpublished"].includes(c.listing_status));
export const isPosted = (c) => c.kind !== "lead" && !!c.ids?.horse_id && (!!c.marked_posted_at || (c.stage === "ongoing" && !!c.ids?.application_id));

// posted=true: Mark as Posted (→ Ongoing); posted=false: correct a mistaken Posted status.
export function MarkPostedDialog({ c, posted, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const app = !!c.ids?.application_id;
  const go = async () => {
    setBusy(true);
    try {
      const r = await markPosted(c.key, posted);
      const where = { ongoing: "now in Ongoing", ready: "back in Ready to Post", contracts_ads: "back in Contracts & Ads", completed: "now in Done" }[r?.stage] || "updated";
      toast.success(posted ? `${c.name} marked as posted — ${where}` : `${c.name} is no longer marked as posted — ${where}`);
      onDone?.(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the posted status"); } finally { setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <AlertDialogContent data-testid="mark-posted-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{posted ? `Mark ${c.name} as posted?` : `Undo "Posted" for ${c.name}?`}</AlertDialogTitle>
          <AlertDialogDescription asChild><div className="text-sm text-stone-600 space-y-2 text-left">
            {posted ? <>
              <p>Use this after you've posted the ad in the Facebook Group. The horse moves to <strong>Ongoing</strong> and the date and time are recorded.</p>
              {app && <p className="font-semibold text-stone-800">The ad will also be published to the {c.is_test ? "TEST" : "LIVE"} website.</p>}
            </> : <>
              <p>The Facebook Group status goes back to what it was before and the horse leaves Ongoing.</p>
              {app && <p className="font-semibold text-stone-800">The ad will be unpublished from the website (moved back to Ready to Post). Nothing is deleted.</p>}
            </>}
            <p className="text-xs">Posting, bidding start and end dates, contract, ad, media and the Facebook post itself are not changed.</p>
          </div></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="mark-posted-cancel" disabled={busy}>Cancel</AlertDialogCancel>
          <Button data-testid="mark-posted-confirm" disabled={busy} onClick={go} className={posted ? "bg-[#1877F2] hover:bg-[#166FE5] text-white" : ""}>
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{posted ? "Mark as Posted" : "Undo Posted"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
